import React from 'react'
import ReactDOM from 'react-dom/client'
import './index.css'
import App from './App'
import { initSentry, Sentry } from './lib/sentry'
import { initAnalytics } from './lib/analytics'
import { initClarity } from './lib/clarity'
import { AuthProvider } from './hooks/useAuth'
import { clearAppBadge } from './lib/push'

// Keep Sentry eager so startup crashes are reported.
initSentry()

// Defer non-critical analytics until the browser is idle.
function deferIdle(fn: () => void) {
  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    (window as Window & { requestIdleCallback: (cb: () => void, opts?: { timeout?: number }) => number })
      .requestIdleCallback(fn, { timeout: 2000 })
  } else {
    setTimeout(fn, 2000)
  }
}
/**
 * Delete the figure cache from before the 2026-08-19 rename.
 *
 * The runtime cache moved from 'acesso-figures' to 'tutor-figures' to orphan
 * the 404s that CacheFirst had stored for figures uploaded after a student
 * first saw the question (see the runtimeCaching comment in vite.config.ts).
 * Orphaning them is what fixes the bug, but Workbox only prunes entries
 * inside a cache it still owns: a bucket nobody names any more is simply
 * abandoned, and would sit on the student's device holding up to 600 images
 * until the browser evicted it under storage pressure. On the low-end phones
 * this app targets, that is real space.
 *
 * Unguarded on purpose. caches.delete() resolves false in about a
 * millisecond once the bucket is gone, which is cheaper than introducing
 * another storage key to remember that we did it, and it leaves nothing
 * behind to clean up later.
 */
function dropRenamedFigureCache() {
  if (typeof caches === 'undefined') return
  caches.delete('acesso-figures').catch(() => { /* nothing to do */ })
}

deferIdle(() => {
  initAnalytics()
  initClarity()
  dropRenamedFigureCache()
})

/**
 * Clear the app-icon badge whenever the student is actually looking at the
 * app (2026-08-28). The service worker sets it on push and clears it on a
 * notification tap; this covers the student who opens the app directly.
 *
 * On visibilitychange as well as at startup, because an installed PWA is
 * usually backgrounded rather than closed, so a cold start may not happen for
 * days and the badge would otherwise persist across every push in between.
 */
void clearAppBadge()
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') void clearAppBadge()
})

// Keep stack traces out of student-facing screens.
function ErrorFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-gray-50">
      <div className="max-w-sm text-center bg-white rounded-xl border border-gray-100 shadow-sm p-8">
        <h1 className="text-lg font-semibold text-gray-900 mb-2">Algo correu mal.</h1>
        <p className="text-sm text-gray-500 leading-relaxed mb-5">
          Já fomos avisados. Recarrega a página e voltamos a isto.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="w-full py-3 rounded-xl text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
        >
          Recarregar
        </button>
      </div>
    </div>
  )
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <AuthProvider>
        <App />
      </AuthProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
)
